import React, { useState, useRef, useEffect } from 'react';
import {
  ClipboardList,
  Plus,
  Trash2,
  Edit3,
  Copy,
  Link,
  ChevronUp,
  ChevronDown,
  Check,
  X,
  Eye,
  FileText,
  GripVertical,
  ExternalLink,
  Inbox,
  AlertCircle,
  Download,
  Upload,
} from 'lucide-react';
import type { SurveyTemplate, SurveyField, SurveyFieldType } from '../types';
import { useAuthStore } from '../store/useAuthStore';
import { useSurveyStore } from '../store/useSurveyStore';
import { uid } from '../utils/helpers';
import SurveyFillPage from './SurveyFillPage';
import SurveyResponses from './SurveyResponses';
import SurveyAnalysis from './SurveyAnalysis';
import { toast } from 'sonner';

const FIELD_TYPE_LABELS: Record<SurveyFieldType, string> = {
  text: 'Tekst (krótki)',
  textarea: 'Tekst (długi)',
  number: 'Liczba',
  select: 'Lista rozwijana',
  multiselect: 'Wielokrotny wybór',
  radio: 'Jednokrotny wybór',
  checkbox: 'Tak / Nie',
  range: 'Suwak (zakres)',
};

export default function SurveyManager() {
  const profile = useAuthStore((s) => s.currentProfile());
  const {
    surveys,
    responses,
    loading,
    createSurvey,
    updateSurvey,
    deleteSurvey,
    addField,
    updateField,
    removeField,
    reorderFields,
    getSurveysByTrainer,
    getResponsesBySurvey,
    submitResponse,
    createSurveyAPI,
    deleteSurveyAPI,
    fetchSurveys,
  } = useSurveyStore();
  // Fetch surveys from API on mount
  useEffect(() => {
    fetchSurveys();
  }, []);

  const [editingSurveyId, setEditingSurveyId] = useState<string | null>(null);
  const [previewSurveyId, setPreviewSurveyId] = useState<string | null>(null);
  const [viewResponsesSurveyId, setViewResponsesSurveyId] = useState<string | null>(null);
  const [analyzeState, setAnalyzeState] = useState<{ surveyId: string; responseId: string } | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [newFieldForm, setNewFieldForm] = useState(false);
  const [editSurveyMeta, setEditSurveyMeta] = useState<{ title: string; description: string } | null>(null);

  // New field state
  const [nfType, setNfType] = useState<SurveyFieldType>('text');
  const [nfLabel, setNfLabel] = useState('');
  const [nfPlaceholder, setNfPlaceholder] = useState('');
  const [nfRequired, setNfRequired] = useState(true);
  const [nfOptions, setNfOptions] = useState('');
  const [nfMin, setNfMin] = useState<number | ''>('');
  const [nfMax, setNfMax] = useState<number | ''>('');
  const [nfStep, setNfStep] = useState<number | ''>('');
  const [nfDefaultValue, setNfDefaultValue] = useState('');
  const [creating, setCreating] = useState(false);
  const importSurveyRef = useRef<HTMLInputElement>(null);

  if (!profile || profile.role !== 'trainer') {
    return (
      <div className="max-w-3xl mx-auto text-center py-16 animate-fade-in">
        <AlertCircle size={48} className="mx-auto text-slate-400 mb-4" />
        <h2 className="text-lg font-bold text-slate-500 dark:text-slate-400 mb-1">
          Funkcja dostępna tylko dla trenerów
        </h2>
        <p className="text-sm text-slate-400">
          Zaloguj się jako trener, aby tworzyć ankiety dla klientów.
        </p>
      </div>
    );
  }

  const mySurveys = getSurveysByTrainer(profile.id);

  // If previewing
  if (previewSurveyId) {
    const survey = mySurveys.find((s) => s.id === previewSurveyId);
    if (!survey) {
      setPreviewSurveyId(null);
      return null;
    }
    return (
      <div className="animate-fade-in">
        <button
          onClick={() => setPreviewSurveyId(null)}
          className="mb-4 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
        >
          <X size={14} />
          Zamknij podgląd
        </button>
        <SurveyFillPage surveyId={survey.id} isPreview />
      </div>
    );
  }

  // If viewing responses
  if (viewResponsesSurveyId) {
    const survey = mySurveys.find((s) => s.id === viewResponsesSurveyId);
    if (!survey) {
      setViewResponsesSurveyId(null);
      return null;
    }

    // If analyzing a specific response
    if (analyzeState && analyzeState.surveyId === viewResponsesSurveyId) {
      return (
        <div className="animate-fade-in">
          <button
            onClick={() => setAnalyzeState(null)}
            className="mb-4 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
          >
            <X size={14} />
            Powrót do odpowiedzi
          </button>
          <SurveyAnalysis
            surveyId={analyzeState.surveyId}
            responseId={analyzeState.responseId}
            onClose={() => setAnalyzeState(null)}
          />
        </div>
      );
    }

    return (
      <div className="animate-fade-in">
        <button
          onClick={() => setViewResponsesSurveyId(null)}
          className="mb-4 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
        >
          <X size={14} />
          Powrót do listy ankiet
        </button>
        <SurveyResponses
          surveyId={survey.id}
          onAnalyze={(responseId) => setAnalyzeState({ surveyId: survey.id, responseId })}
        />
      </div>
    );
  }

  const generateLink = (survey: SurveyTemplate) => {
    const base = window.location.origin + window.location.pathname;
    // Use publicToken for the share link - this is what the public API expects
    return `${base}#survey/${survey.publicToken || survey.id}`;
  };

  const copyLink = (survey: SurveyTemplate) => {
    const link = generateLink(survey);
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(survey.id);
      setTimeout(() => setCopiedLink(null), 2000);
    });
  };


  const handleCreateSurvey = async () => {
    if (creating) return; // prevent duplicate clicks
    setCreating(true);
    try {
      await createSurveyAPI('Ankieta dla klienta');
    } finally {
      setCreating(false);
    }
  };

  const resetNewFieldForm = () => {
    setNfType('text');
    setNfLabel('');
    setNfPlaceholder('');
    setNfRequired(true);
    setNfOptions('');
    setNfMin('');
    setNfMax('');
    setNfStep('');
    setNfDefaultValue('');
    setNewFieldForm(false);
  };

  const handleAddField = (surveyId: string) => {
    if (!nfLabel.trim()) return;
    const field: SurveyField = {
      id: uid(),
      type: nfType,
      label: nfLabel.trim(),
      placeholder: nfPlaceholder || undefined,
      required: nfRequired,
      options: ['select', 'multiselect', 'radio'].includes(nfType)
        ? nfOptions.split('\n').map((o) => o.trim()).filter(Boolean)
        : undefined,
      min: nfMin !== '' ? Number(nfMin) : undefined,
      max: nfMax !== '' ? Number(nfMax) : undefined,
      step: nfStep !== '' ? Number(nfStep) : undefined,
      defaultValue: nfDefaultValue || undefined,
    };
    addField(surveyId, field);
    resetNewFieldForm();
  };

  const handleDeleteSurvey = (surveyId: string) => {
    if (window.confirm('Na pewno chcesz usunąć tę ankietę? Wszystkie odpowiedzi zostaną usunięte.')) {
      deleteSurveyAPI(surveyId);
      if (editingSurveyId === surveyId) setEditingSurveyId(null);
    }
  };

  // ── Export survey (template + responses) to JSON ──
  const handleExportSurvey = (surveyId: string) => {
    const survey = mySurveys.find((s) => s.id === surveyId);
    if (!survey) return;
    const surveyResponses = getResponsesBySurvey(surveyId);
    const exportData = {
      _type: 'apex-survey-export',
      _version: 1,
      survey,
      responses: surveyResponses,
      exportedAt: new Date().toISOString(),
    };
    const data = JSON.stringify(exportData, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ankieta_${survey.title.replace(/[^a-zA-Z0-9ąęóśłżźćńĄĘÓŚŁŻŹĆŃ _-]/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Import survey from JSON file ──
  const handleImportSurvey = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string);
        if (parsed._type === 'apex-survey-export' && parsed.survey) {
          // Import the survey as a new survey for this trainer
          const importedSurvey = parsed.survey;
          // const newSurvey = createSurvey(profile!.id, importedSurvey.title + ' (import)');
          await createSurveyAPI(importedSurvey.title + ' (import)', importedSurvey.description);
          // fetchSurveys automatycznie odświeży listę
          // Import responses if any
          if (Array.isArray(parsed.responses)) {
            // responses import skipped for now (API needed)
          }
          alert(`Zaimportowano ankietę "${importedSurvey.title}" z ${parsed.responses?.length || 0} odpowiedziami.`);
        } else {
          alert('Plik nie zawiera prawidłowej ankiety. Upewnij się, że to plik wyeksportowany z APEX.');
        }
      } catch (err) {
        toast.error('Błąd importu ankiety');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  // ── Editing survey detail ──
  const editingSurvey = editingSurveyId ? mySurveys.find((s) => s.id === editingSurveyId) : null;

  if (editingSurvey) {
    const responseCount = (editingSurvey as any).responseCount ?? 0;

    return (
      <div className="max-w-4xl mx-auto animate-fade-in">
        {/* Back + header */}
        <button
          onClick={() => { setEditingSurveyId(null); setEditingFieldId(null); setNewFieldForm(false); setEditSurveyMeta(null); }}
          className="mb-4 flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
        >
          <X size={14} />
          Powrót do listy
        </button>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 mb-6">
          {/* Survey title / description edit */}
          {editSurveyMeta ? (
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Tytuł ankiety</label>
                <input
                  type="text"
                  value={editSurveyMeta.title}
                  onChange={(e) => setEditSurveyMeta({ ...editSurveyMeta, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Opis</label>
                <textarea
                  value={editSurveyMeta.description}
                  onChange={(e) => setEditSurveyMeta({ ...editSurveyMeta, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition resize-y"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    updateSurvey(editingSurvey.id, { title: editSurveyMeta.title, description: editSurveyMeta.description });
                    setEditSurveyMeta(null);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 transition flex items-center gap-1"
                >
                  <Check size={12} /> Zapisz
                </button>
                <button
                  onClick={() => setEditSurveyMeta(null)}
                  className="px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Anuluj
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">{editingSurvey.title}</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{editingSurvey.description}</p>
              </div>
              <button
                onClick={() => setEditSurveyMeta({ title: editingSurvey.title, description: editingSurvey.description })}
                className="p-2 rounded-lg text-slate-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition"
                title="Edytuj tytuł i opis"
              >
                <Edit3 size={16} />
              </button>
            </div>
          )}

          {/* Link + actions */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => copyLink(editingSurvey)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300 text-xs font-medium hover:bg-brand-100 dark:hover:bg-brand-900 transition"
            >
              {copiedLink === editingSurvey.id ? <Check size={12} /> : <Link size={12} />}
              {copiedLink === editingSurvey.id ? 'Skopiowano!' : 'Kopiuj link do ankiety'}
            </button>
            <button
              onClick={() => setPreviewSurveyId(editingSurvey.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <Eye size={12} /> Podgląd
            </button>
            <button
              onClick={() => setViewResponsesSurveyId(editingSurvey.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <Inbox size={12} /> Odpowiedzi ({responseCount})
            </button>
            <button
              onClick={() => handleExportSurvey(editingSurvey.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition"
              title="Eksportuj ankietę z odpowiedziami do pliku"
            >
              <Download size={12} /> Eksportuj
            </button>
          </div>

          {/* Link display */}
          <div className="mb-6 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1 font-semibold">Link do ankiety</p>
            <p className="text-xs text-brand-600 dark:text-brand-400 break-all font-mono select-all">
              {generateLink(editingSurvey)}
            </p>
          </div>

          {/* Fields list */}
          <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Pytania ({editingSurvey.fields.length})
          </h3>

          <div className="space-y-2 mb-4">
            {editingSurvey.fields.map((field, idx) => (
              <FieldEditor
                key={field.id}
                field={field}
                index={idx}
                total={editingSurvey.fields.length}
                isEditing={editingFieldId === field.id}
                onStartEdit={() => setEditingFieldId(field.id)}
                onStopEdit={() => setEditingFieldId(null)}
                onUpdate={(updates) => updateField(editingSurvey.id, field.id, updates)}
                onRemove={() => removeField(editingSurvey.id, field.id)}
                onMoveUp={() => reorderFields(editingSurvey.id, idx, idx - 1)}
                onMoveDown={() => reorderFields(editingSurvey.id, idx, idx + 1)}
              />
            ))}
          </div>

          {/* Add new field */}
          {newFieldForm ? (
            <NewFieldForm
              nfType={nfType} setNfType={setNfType}
              nfLabel={nfLabel} setNfLabel={setNfLabel}
              nfPlaceholder={nfPlaceholder} setNfPlaceholder={setNfPlaceholder}
              nfRequired={nfRequired} setNfRequired={setNfRequired}
              nfOptions={nfOptions} setNfOptions={setNfOptions}
              nfMin={nfMin} setNfMin={setNfMin}
              nfMax={nfMax} setNfMax={setNfMax}
              nfStep={nfStep} setNfStep={setNfStep}
              nfDefaultValue={nfDefaultValue} setNfDefaultValue={setNfDefaultValue}
              onAdd={() => handleAddField(editingSurvey.id)}
              onCancel={resetNewFieldForm}
            />
          ) : (
            <button
              onClick={() => setNewFieldForm(true)}
              className="w-full py-3 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 hover:text-brand-500 hover:border-brand-400 text-sm font-medium transition flex items-center justify-center gap-2"
            >
              <Plus size={16} /> Dodaj pytanie
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Main survey list ──
  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-950 flex items-center justify-center">
          <ClipboardList size={22} className="text-brand-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold">Ankiety dla klientów</h2>
          <p className="text-xs text-slate-400">
            Twórz ankiety i wysyłaj link klientom - odpowiedzi zobaczysz tutaj
          </p>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <button
            onClick={() => importSurveyRef.current?.click()}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-brand-400 text-sm font-medium transition-all bg-white dark:bg-slate-900"
            title="Wczytaj ankietę z pliku"
          >
            <Upload size={16} className="text-brand-500" />
            <span className="hidden sm:inline">Wczytaj</span>
          </button>
          <button
            onClick={handleCreateSurvey}
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} /> {creating ? 'Tworzenie...' : 'Nowa ankieta'}
          </button>
          <input
            ref={importSurveyRef}
            type="file"
            accept=".json"
            onChange={handleImportSurvey}
            className="hidden"
          />
        </div>
      </div>

      {mySurveys.length === 0 ? (
        <div className="text-center py-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4">
            <ClipboardList size={32} className="text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-500 dark:text-slate-400 mb-1">Brak ankiet</h3>
          <p className="text-sm text-slate-400 mb-4">
            Stwórz pierwszą ankietę i wyślij link klientowi
          </p>
          <button
            onClick={handleCreateSurvey}
            disabled={creating}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} /> {creating ? 'Tworzenie...' : 'Stwórz ankietę'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {mySurveys.map((survey) => {
            const resCount = (survey as any).responseCount ?? 0;
            return (
              <div
                key={survey.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden hover:border-brand-400 dark:hover:border-brand-500 hover:shadow-lg transition-all"
              >
                <div className="p-4 pb-3">
                  <h3 className="font-bold text-sm mb-1">{survey.title}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mb-2">
                    {survey.description}
                  </p>
                  <div className="flex gap-3 text-[10px] text-slate-400">
                    <span>{survey.fields.length} pytań</span>
                    <span>{resCount} odpowiedzi</span>
                    <span>
                      {new Date(survey.updatedAt).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                </div>

                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-1">
                  <button
                    onClick={() => setEditingSurveyId(survey.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition font-medium"
                  >
                    <Edit3 size={12} /> <span className="hidden sm:inline">Edytuj</span>
                  </button>
                  <button
                    onClick={() => copyLink(survey)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition font-medium"
                  >
                    {copiedLink === survey.id ? <Check size={12} /> : <Link size={12} />}
                    <span className="hidden sm:inline">{copiedLink === survey.id ? 'Skopiowano!' : 'Link'}</span>
                  </button>
                  <button
                    onClick={() => setPreviewSurveyId(survey.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition font-medium"
                  >
                    <Eye size={12} /> <span className="hidden sm:inline">Podgląd</span>
                  </button>
                  <button
                    onClick={() => setViewResponsesSurveyId(survey.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition font-medium"
                  >
                    <Inbox size={12} /> Odp. ({resCount})
                  </button>
                  <button
                    onClick={() => handleExportSurvey(survey.id)}
                    className="flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-950 transition font-medium"
                    title="Eksportuj ankietę do pliku"
                  >
                    <Download size={12} /> <span className="hidden sm:inline">Eksport</span>
                  </button>
                  <button
                    onClick={() => handleDeleteSurvey(survey.id)}
                    className="ml-auto p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition"
                    title="Usuń"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Field Editor Component ──────────────────

interface FieldEditorProps {
  field: SurveyField;
  index: number;
  total: number;
  isEditing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onUpdate: (updates: Partial<SurveyField>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function FieldEditor({
  field,
  index,
  total,
  isEditing,
  onStartEdit,
  onStopEdit,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
}: FieldEditorProps) {
  const [localLabel, setLocalLabel] = useState(field.label);
  const [localPlaceholder, setLocalPlaceholder] = useState(field.placeholder || '');
  const [localRequired, setLocalRequired] = useState(field.required);
  const [localOptions, setLocalOptions] = useState((field.options || []).join('\n'));
  const [localMin, setLocalMin] = useState<number | ''>(field.min ?? '');
  const [localMax, setLocalMax] = useState<number | ''>(field.max ?? '');
  const [localStep, setLocalStep] = useState<number | ''>(field.step ?? '');
  const [localType, setLocalType] = useState<SurveyFieldType>(field.type);
  const [localDefault, setLocalDefault] = useState(field.defaultValue || '');

  const save = () => {
    onUpdate({
      type: localType,
      label: localLabel.trim(),
      placeholder: localPlaceholder || undefined,
      required: localRequired,
      options: ['select', 'multiselect', 'radio'].includes(localType)
        ? localOptions.split('\n').map((o) => o.trim()).filter(Boolean)
        : undefined,
      min: localMin !== '' ? Number(localMin) : undefined,
      max: localMax !== '' ? Number(localMax) : undefined,
      step: localStep !== '' ? Number(localStep) : undefined,
      defaultValue: localDefault || undefined,
    });
    onStopEdit();
  };

  if (isEditing) {
    return (
      <div className="p-4 rounded-xl border-2 border-brand-400 dark:border-brand-500 bg-white dark:bg-slate-900 space-y-3 animate-fade-in">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Etykieta</label>
            <input
              type="text"
              value={localLabel}
              onChange={(e) => setLocalLabel(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Typ pola</label>
            <select
              value={localType}
              onChange={(e) => setLocalType(e.target.value as SurveyFieldType)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
            >
              {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Placeholder</label>
          <input
            type="text"
            value={localPlaceholder}
            onChange={(e) => setLocalPlaceholder(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
          />
        </div>

        {['select', 'multiselect', 'radio'].includes(localType) && (
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">
              Opcje (każda w nowej linii)
            </label>
            <textarea
              value={localOptions}
              onChange={(e) => setLocalOptions(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition resize-y font-mono"
            />
          </div>
        )}

        {['number', 'range'].includes(localType) && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Min</label>
              <input
                type="number"
                value={localMin}
                onChange={(e) => setLocalMin(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Max</label>
              <input
                type="number"
                value={localMax}
                onChange={(e) => setLocalMax(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>
            {localType === 'range' && (
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Krok</label>
                <input
                  type="number"
                  value={localStep}
                  onChange={(e) => setLocalStep(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
              </div>
            )}
          </div>
        )}

        {localType === 'range' && (
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Wartość domyślna</label>
            <input
              type="number"
              value={localDefault}
              onChange={(e) => setLocalDefault(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
            />
          </div>
        )}

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={localRequired}
              onChange={(e) => setLocalRequired(e.target.checked)}
              className="accent-brand-500"
            />
            Wymagane
          </label>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            onClick={save}
            className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 transition flex items-center gap-1"
          >
            <Check size={12} /> Zapisz
          </button>
          <button
            onClick={onStopEdit}
            className="px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            Anuluj
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600 transition group">
      <span className="text-[10px] font-mono text-slate-400 w-5 text-right">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{field.label}</span>
          {field.required && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 dark:bg-red-950 text-red-600 dark:text-red-400 font-semibold">
              wymagane
            </span>
          )}
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 font-medium">
            {FIELD_TYPE_LABELS[field.type]}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
        <button
          onClick={onMoveUp}
          disabled={index === 0}
          className="p-1 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30 transition"
        >
          <ChevronUp size={14} />
        </button>
        <button
          onClick={onMoveDown}
          disabled={index === total - 1}
          className="p-1 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30 transition"
        >
          <ChevronDown size={14} />
        </button>
        <button
          onClick={onStartEdit}
          className="p-1 rounded text-slate-400 hover:text-brand-500 transition"
        >
          <Edit3 size={14} />
        </button>
        <button
          onClick={() => {
            if (window.confirm(`Usunąć pytanie "${field.label}"?`)) onRemove();
          }}
          className="p-1 rounded text-slate-400 hover:text-red-500 transition"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── New Field Form ──────────────────────────

interface NewFieldFormProps {
  nfType: SurveyFieldType;
  setNfType: (v: SurveyFieldType) => void;
  nfLabel: string;
  setNfLabel: (v: string) => void;
  nfPlaceholder: string;
  setNfPlaceholder: (v: string) => void;
  nfRequired: boolean;
  setNfRequired: (v: boolean) => void;
  nfOptions: string;
  setNfOptions: (v: string) => void;
  nfMin: number | '';
  setNfMin: (v: number | '') => void;
  nfMax: number | '';
  setNfMax: (v: number | '') => void;
  nfStep: number | '';
  setNfStep: (v: number | '') => void;
  nfDefaultValue: string;
  setNfDefaultValue: (v: string) => void;
  onAdd: () => void;
  onCancel: () => void;
}

function NewFieldForm({
  nfType, setNfType,
  nfLabel, setNfLabel,
  nfPlaceholder, setNfPlaceholder,
  nfRequired, setNfRequired,
  nfOptions, setNfOptions,
  nfMin, setNfMin,
  nfMax, setNfMax,
  nfStep, setNfStep,
  nfDefaultValue, setNfDefaultValue,
  onAdd, onCancel,
}: NewFieldFormProps) {
  return (
    <div className="p-4 rounded-xl border-2 border-dashed border-brand-400 dark:border-brand-500 bg-brand-50/50 dark:bg-brand-950/30 space-y-3 animate-fade-in">
      <h4 className="text-xs font-semibold text-brand-600 uppercase tracking-wider">Nowe pytanie</h4>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Etykieta *</label>
          <input
            type="text"
            value={nfLabel}
            onChange={(e) => setNfLabel(e.target.value)}
            placeholder="Np. Ile masz lat?"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Typ pola</label>
          <select
            value={nfType}
            onChange={(e) => setNfType(e.target.value as SurveyFieldType)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
          >
            {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Placeholder</label>
        <input
          type="text"
          value={nfPlaceholder}
          onChange={(e) => setNfPlaceholder(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
        />
      </div>

      {['select', 'multiselect', 'radio'].includes(nfType) && (
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">
            Opcje (każda w nowej linii)
          </label>
          <textarea
            value={nfOptions}
            onChange={(e) => setNfOptions(e.target.value)}
            rows={4}
            placeholder={"Opcja 1\nOpcja 2\nOpcja 3"}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition resize-y font-mono"
          />
        </div>
      )}

      {['number', 'range'].includes(nfType) && (
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Min</label>
            <input
              type="number"
              value={nfMin}
              onChange={(e) => setNfMin(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Max</label>
            <input
              type="number"
              value={nfMax}
              onChange={(e) => setNfMax(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
            />
          </div>
          {nfType === 'range' && (
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Krok</label>
              <input
                type="number"
                value={nfStep}
                onChange={(e) => setNfStep(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
              />
            </div>
          )}
        </div>
      )}

      {nfType === 'range' && (
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Wartość domyślna</label>
          <input
            type="number"
            value={nfDefaultValue}
            onChange={(e) => setNfDefaultValue(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
          />
        </div>
      )}

      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={nfRequired}
          onChange={(e) => setNfRequired(e.target.checked)}
          className="accent-brand-500"
        />
        Wymagane
      </label>

      <div className="flex gap-2 pt-1">
        <button
          onClick={onAdd}
          disabled={!nfLabel.trim()}
          className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 transition flex items-center gap-1 disabled:opacity-50"
        >
          <Plus size={12} /> Dodaj
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        >
          Anuluj
        </button>
      </div>
    </div>
  );
}
